/**
 * RevenueCat billing endpoints (AB#279).
 */

import { api, ApiError } from './client';
import type { ApiResponse } from '../types';

export type BillingTier = 'basic' | 'pro' | 'ultimate';
export type BillingPlan = 'free' | BillingTier;

/**
 * One entry in `BillingStatus.subscriptions` — the customer's ACTIVE RevenueCat
 * subscription set (AB#581). Maps 1:1 to `services/revenuecat.rs::Subscription`
 * filtered to `is_active()`. The list is the page's source of truth; the stored
 * `plan` is only a label. Fields mirror the RC v2 wire format.
 */
export interface SubscriptionSummary {
  id: string;
  /** Product-id translation (basic/pro/ultimate/seat), never env overrides. */
  kind: 'plan' | 'seat' | 'unknown';
  /** Set for `kind === 'plan'`; null otherwise. */
  plan: BillingTier | null;
  /** RC status verbatim (active | trialing | in_grace_period | in_billing_retry | ...). */
  status: string;
  /** 'will_renew' | 'will_not_renew' | ... — drives the "Cancels {date}" state. */
  auto_renewal_status: string;
  /** Epoch **milliseconds** the current period ends, or null. */
  current_period_ends_at: number | null;
  /** RC portal URL for this sub; '' for store-managed (App/Play) subs. */
  management_url: string;
  /** 'rc_billing' | 'stripe' | 'app_store' | 'play_store' | ''. */
  store: string;
}

export interface BillingStatus {
  plan: BillingPlan;
  app_user_id: string;
  public_api_key: string;
  /**
   * Per-user RevenueCat manage/portal URL (billing.rs `manage_url_for`), or
   * null for a free user or when the RC lookup fails — that lookup fails
   * OPEN, so `null` is a normal, expected value, not an error state (AB#503).
   */
  manage_url: string | null;
  basic_price: string;
  pro_price: string;
  /** AB#1061: Ultimate monthly price; absent on backends that predate the tier. */
  ultimate_price?: string;
  /** Price per additional account seat (AB#334), e.g. "$1.99". */
  seat_price: string;
  /** RevenueCat product identifier for the seat subscription. */
  seat_product_id: string;
  /**
   * Plan actually in effect after comps/overrides (AB#464). Usually equals
   * `plan`; a comped user sees their granted tier here while `plan` stays 'free'
   * (no paid RC sub). Additive — older clients ignore it.
   */
  effective_plan: BillingPlan;
  /** True when `effective_plan` is complimentary (comp/promo code), not paid (AB#464). */
  comp: boolean;
  /** Store label of the primary active sub, or null. Additive (AB#581). */
  store?: string | null;
  /** Complimentary plan granted, separate from paid `plan`. Additive (AB#581). */
  comp_plan?: BillingPlan | null;
  /** Epoch ms the primary sub's current period ends, or null. Additive (AB#581). */
  current_period_ends_at?: number | null;
  /** Whether the primary sub auto-renews. Additive (AB#581). */
  will_renew?: boolean | null;
  /**
   * The customer's ACTIVE RC subscription set (AB#581). Additive — absent/empty
   * on older backends, and the frontend treats a missing value as `[]`.
   */
  subscriptions?: SubscriptionSummary[];
}

/**
 * Derived subscription flags for the /billing card (AB#581). Pure — depends only
 * on `status`, so it's the single testable place mismatch/overlap live.
 */
export interface SubscriptionState {
  /** ACTIVE-set the backend returned (never null). */
  activeSubs: SubscriptionSummary[];
  /** Just the `kind === 'plan'` subs. */
  planSubs: SubscriptionSummary[];
  /** Any live plan sub exists. */
  hasLivePaidSub: boolean;
  /** RC holds a live plan sub but the stored plan is still `free` — Rick's bug. */
  mismatch: boolean;
  /** >1 live plan sub → likely double-billed. */
  overlap: boolean;
  /** ≥1 store-managed (App/Play) sub the web cancel can't touch. */
  hasStoreManaged: boolean;
}

/** True for subs the web cancel endpoint cannot reach (RC can't server-cancel store subs). */
export function isStoreManaged(sub: SubscriptionSummary): boolean {
  return sub.store === 'app_store' || sub.store === 'play_store';
}

/** Derive the subscription-card flags from a billing status (AB#581). */
export function deriveSubscriptionState(status: BillingStatus | undefined): SubscriptionState {
  const activeSubs = status?.subscriptions ?? [];
  const planSubs = activeSubs.filter((s) => s.kind === 'plan');
  const hasLivePaidSub = planSubs.length > 0;
  return {
    activeSubs,
    planSubs,
    hasLivePaidSub,
    mismatch: hasLivePaidSub && (status?.plan ?? 'free') === 'free',
    overlap: planSubs.length > 1,
    hasStoreManaged: activeSubs.some(isStoreManaged),
  };
}

/** Body for `POST /billing/redeem` (AB#464). */
export interface RedeemCodeRequest {
  code: string;
}

/** 200 payload of a successful redeem (AB#464). */
export interface RedeemCodeResponse {
  /** Plan now in effect after redeeming. */
  effective_plan: BillingPlan;
  /** True when the redeemed code grants a complimentary plan. */
  comp: boolean;
}

/** Distinct 4xx rejection codes returned in `error.code` by `POST /billing/redeem`. */
export type RedeemErrorCode = 'invalid' | 'expired' | 'exhausted' | 'already_redeemed';

/**
 * `error.details` of a 402 capability denial (AB#465): an action needs a higher
 * plan than the user has. Same 402 shape family as `seat_limit_reached`, but the
 * remedy is a plan upgrade, not a seat purchase. All fields optional — the hint
 * set varies by gated feature.
 */
export interface UpgradeRequiredDetails {
  /** Plan the feature requires, e.g. "pro". Defaults to "pro" if unspecified. */
  required_plan: string;
  /** The user's current plan, if the backend supplies it. */
  current_plan?: string;
  /** Which capability/feature was gated, for a specific message. */
  feature?: string;
  /** Human message from the backend, if any. */
  message?: string;
}

/**
 * If an error is a 402 capability denial (AB#465) — an action needs a higher
 * plan — pull the upgrade hint off it. Returns null for anything else, including
 * the sibling 402 `seat_limit_reached` (a seat purchase, handled on /team).
 */
export function parseUpgradeRequired(error: unknown): UpgradeRequiredDetails | null {
  if (!(error instanceof ApiError) || error.status !== 402) return null;
  if (error.data.code === 'seat_limit_reached') return null;
  const d = (error.data.details ?? {}) as Record<string, unknown>;
  return {
    required_plan: typeof d.required_plan === 'string' ? d.required_plan : 'pro',
    current_plan: typeof d.current_plan === 'string' ? d.current_plan : undefined,
    feature: typeof d.feature === 'string' ? d.feature : undefined,
    message: typeof d.message === 'string' ? d.message : error.data.error || undefined,
  };
}

/** Friendly upsell line for a capability denial, e.g. "Upgrade to Pro to use custom domains." */
export function upgradeRequiredMessage(details: UpgradeRequiredDetails): string {
  if (details.message) return details.message;
  const plan = details.required_plan
    ? details.required_plan.charAt(0).toUpperCase() + details.required_plan.slice(1)
    : 'Pro';
  const what = details.feature ? ` to use ${details.feature}` : '';
  return `Upgrade to ${plan}${what}.`;
}

export interface CheckoutResponse {
  tier: string;
  /** RevenueCat checkout URL (empty until the tier is configured in the dashboard). */
  url: string;
  app_user_id: string;
}

/**
 * `error.details` payload of the 409 `already_subscribed` checkout error (AB#472):
 * the requested plan still has an ACTIVE subscription set to cancel at period end.
 * The user should reactivate instead of buying again.
 */
export interface AlreadySubscribedDetails {
  /** Epoch **milliseconds** the still-active sub ends, or null if RC didn't supply it. */
  current_period_ends_at: number | null;
  /** RevenueCat manage/reactivate URL; may be "" if REVENUECAT_MANAGE_URL is unset. */
  manage_url: string;
}

/**
 * `error.details` payload of the 409 `plan_switch_required` checkout error (AB#467):
 * the user has an ACTIVE, renewing subscription on a DIFFERENT paid plan than the
 * one they clicked. RC Web Billing handles plan changes in the customer portal, so
 * the user should switch there instead of buying a second plan.
 */
export interface PlanSwitchDetails {
  /** The plan the user is currently on, e.g. "basic". */
  from: string;
  /** The plan they tried to buy, e.g. "pro". */
  to: string;
  /** RevenueCat manage/switch URL; may be "" if REVENUECAT_MANAGE_URL is unset. */
  manage_url: string;
}

/**
 * 200 payload of `POST /billing/subscriptions/cancel` (AB#581). Cancels the whole
 * ACTIVE web-billing set at period end; store-managed subs RC can't cancel come
 * back in `uncancellable` so the UI can tell the user to use the store instead.
 */
export interface CancelSubscriptionsResponse {
  /** How many subscriptions were set to cancel at period end. */
  cancelled: number;
  /** Subs the endpoint couldn't cancel (store-managed). Absent/empty when none. */
  uncancellable?: SubscriptionSummary[];
}

export const billingApi = {
  /** Current plan + upgrade/manage config for the authenticated user. */
  status: () => api.get<ApiResponse<BillingStatus>>('/billing/status'),

  /** Stamp/reuse app_user_id and get the checkout URL for a tier. */
  checkout: (tier: BillingTier) =>
    api.get<ApiResponse<CheckoutResponse>>(`/billing/checkout/${tier}`),

  /**
   * Redeem a promo/comp code (AB#464). Rejects with distinct 4xx `error.code`s:
   * invalid | expired | exhausted | already_redeemed.
   */
  redeem: (code: string) =>
    api.post<ApiResponse<RedeemCodeResponse>>('/billing/redeem', { code } satisfies RedeemCodeRequest),

  /**
   * Force a re-read of the RC subscription set into the stored plan (AB#581).
   * Called automatically once in the mismatch state; the response body is
   * whatever the reconcile produced — callers just refetch status after.
   */
  resync: () => api.post<ApiResponse<BillingStatus>>('/billing/resync', {}),

  /**
   * Cancel the whole active web-billing subscription set at period end (AB#581).
   * Wraps the prod-proven `cancel_customer_subscriptions(CancelScope::All)`;
   * store-managed subs are reported back in `uncancellable`.
   */
  cancelSubscriptions: () =>
    api.post<ApiResponse<CancelSubscriptionsResponse>>('/billing/subscriptions/cancel', {}),
};
